import React, { useEffect, useRef, useState } from "react";
import ImageCircle from "./ImageCircle";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@app/lib/store";
import ChatInput from "./MessageInput";
import Message from "./Message";
import { chatAuthentication, fetchMessages } from "./API";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@common/auth/use-auth";
import Pusher from 'pusher-js';
import Echo from "laravel-echo";
import axios from "axios";
import { addMessage, setMessages } from "@app/lib/slices/chatSlice";

// Function to format date as "Today", "Yesterday", or a readable string
function formatDate(date: Date): string {
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  const isSameDay = (d1: Date, d2: Date) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  if (isSameDay(date, now)) {
    return "Today";
  } else if (isSameDay(date, yesterday)) {
    return "Yesterday";
  } else {
    const oneWeekAgo = new Date(now);
    oneWeekAgo.setDate(now.getDate() - 7);

    if (date > oneWeekAgo) {
      return date.toLocaleDateString("en-US", { weekday: "long" });
    } else {
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
  }
}

// Group messages by their formatted date
function groupMessagesByDate(messages: ChatMessage[]): Record<string, ChatMessage[]> {
  const groupedMessages: Record<string, ChatMessage[]> = {};
  messages.forEach((message) => {
    if (!message.created_at) return;

    const dateKey = formatDate(new Date(message.created_at));
    if (!groupedMessages[dateKey]) {
      groupedMessages[dateKey] = [];
    }
    groupedMessages[dateKey].push(message);
  });
  return groupedMessages;
}

export default function ChatContainer() {
  const dispatch = useDispatch();

  const { currentReciever, messages } = useSelector((state: RootState) => state.chat);
  const { user: sender } = useAuth();
  // const [messages, setMessages] = useState<Record<string, ChatMessage[]>>({});
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [showScrollDown, setShowScrollDown] = useState(true);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    dispatch(setMessages({}));
  }, [currentReciever, dispatch])

  useEffect(() => {
    const handleScroll = () => {
      const el = chatContainerRef.current;
      if (!el) return;

      const isUserAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
      setIsAtBottom(isUserAtBottom);
      setShowScrollDown(!isUserAtBottom);
    };

    const container = chatContainerRef.current;
    container?.addEventListener("scroll", handleScroll);

    return () => {
      container?.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    fetchMessages({ id: currentReciever?.id || 0 })
      .then((data) => {
        dispatch(setMessages(groupMessagesByDate(data.data.messages)));
        setLoading(false);
        setTimeout(scrollToBottom, 50); // Wait for DOM update
      });

    const pusher = new Pusher('6ed01a2426b65664ae9f', {
      cluster: 'eu',
    });

    const channel = pusher.subscribe(`chatify.${sender?.id}`);
    channel.bind('messaging', function (data: any) {
      const message = {
        from_id: data.from_id,
        to_id: data.to_id,
        created_at: data.message.created_at,
        body: data.message.message,
        id: data.message.id,
        seen: data.message.seen,
        attachment: data.message.attachment.file ? JSON.stringify({
          new_name: data.message.attachment.file,
          old_name: data.message.attachment.title,
        }) : undefined,
      } as ChatMessage;

      if (!message.created_at) return;
      dispatch(addMessage(message));

      if (isAtBottom) {
        setTimeout(scrollToBottom, 50); // auto scroll only if user is at bottom
      }
    });

    return () => {
      pusher.unsubscribe(`chatify.${sender?.id}`);
    };
  }, [currentReciever, sender?.id]);


  // useEffect(() => {
  //   fetchMessages({ id: currentReciever?.id || 0 })
  //     .then((data) => {
  //       dispatch(setMessages(groupMessagesByDate(data.data.messages)));
  //       setLoading(false);
  //       scrollToBottom();
  //     });

  //   const pusher = new Pusher('6ed01a2426b65664ae9f', {
  //     cluster: 'eu',
  //   });

  //   const channel = pusher.subscribe(`chatify.${sender?.id}`);
  //   channel.bind('messaging', function (data: any) {
  //     const message = {
  //       from_id: data.from_id,
  //       to_id: data.to_id,
  //       created_at: data.message.created_at,
  //       body: data.message.message,
  //       id: data.message.id,
  //       seen: data.message.seen,
  //       attachment: data.message.attachment.file ? JSON.stringify({
  //         new_name: data.message.attachment.file,
  //         old_name: data.message.attachment.title,
  //       }) : undefined,
  //     } as ChatMessage;

  //     // console.log('=======================================================================');

  //     // console.log(data);


  //     if (!message.created_at) return;

  //     // const dateKey = formatDate(new Date(message.created_at));

  //     dispatch(addMessage(message));

  //     // Update state immutably
  //     // setMessages((prevMessages) => {
  //     //   const updatedMessages = { ...prevMessages }; // Create a new object
  //     //   if (!updatedMessages[dateKey]) {
  //     //     updatedMessages[dateKey] = []; // Initialize if not present
  //     //   }
  //     //   updatedMessages[dateKey] = [...updatedMessages[dateKey], message]; // Create a new array with the new message
  //     //   return updatedMessages;
  //     // });
  //   });

  //   return () => {
  //     pusher.unsubscribe(`chatify.${sender?.id}`);
  //   };
  // }, [currentReciever, sender?.id, loading, dispatch]);


  // useEffect(()=>{

  //   fetchMessages({ id: currentReciever?.id || 0 })
  //   .then(data=>{setMessages(groupMessagesByDate(data.data.messages)); setLoading(false)})

  //   const pusher = new Pusher('6ed01a2426b65664ae9f', {
  //     cluster: 'eu',
  //   })

  //   const channel = pusher.subscribe(`chatify.${sender?.id}`);
  //   channel.bind('messaging', function(data: any){
  //     const message = {
  //       from_id: data.from_id,
  //       to_id: data.to_id,
  //       created_at: data.message.created_at,
  //       body: data.message.message,
  //       id: data.message.id,
  //       seen: data.message.seen,
  //     } as ChatMessage;

  //     console.log(message);


  //     if (!message.created_at) return;

  //     const dateKey = formatDate(new Date(message.created_at));
  //     const messagesTmp = messages;
  //     if (!messagesTmp[dateKey]) {
  //       messagesTmp[dateKey] = [];
  //     }
  //     messagesTmp[dateKey].push(message);
  //     setMessages(messagesTmp);


  //   });

  //   return () => {
  //     pusher.unsubscribe('chatify');
  //   }



  // }, [currentReciever, loading])

  // const { data, isLoading, error } = useQuery({
  //   queryKey: ['getMessages', currentReciever?.id],
  //   queryFn: () => fetchMessages({ id: currentReciever?.id || 0 }),
  //   refetchInterval: 200, // Adjust as needed
  //   enabled: !!currentReciever?.id, // Only run if currentReciever is set
  // });

  // useEffect(() => {
  //   if (data?.data?.messages) {
  //     setMessages(groupMessagesByDate(data.data.messages));
  //   }
  // }, [data]);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    setShowScrollDown(false);
  };



  if (!currentReciever) return null;

  return (
    <div className="flex-grow flex flex-col">
      <ContainerHeader
        userImage={currentReciever.image || currentReciever.avatar || ""}
        userName={
          currentReciever.name ||
          currentReciever.username ||
          (currentReciever.first_name
            ? `${currentReciever.first_name} ${currentReciever.last_name}`
            : "")
        }
      />
      <div className="relative pb-20 flex-grow overflow-y-scroll chat-container" ref={chatContainerRef}>
        {Object.keys(messages || {}).map((dateKey) => (
          <div key={dateKey}>
            <div className="bg-gray-700 text-white w-fit py-8 px-8 rounded-lg text-xs my-10 mx-auto">{dateKey}</div>
            {(messages || {})[dateKey]?.map((message: ChatMessage) => (
              <Message
                key={message.id}
                file={!message.body && !message.message && message.attachment ? JSON.parse(message.attachment) : undefined}
                content={message.body || message.message}
                side={sender?.id === message.from_id ? 0 : 1}
                time={message.created_at ? new Date(message.created_at) : undefined}
                userImage=""
              />
            ))}
          </div>
        ))}

        <div ref={chatEndRef} className="chat-end" />

        {/* 👇 Scroll Down Button */}
        {/* {showScrollDown && (
          <button
            onClick={scrollToBottom}
            className="fixed bottom-30 right-0 w-20 h-20 flex items-center justify-center bg-blue-500 text-white rounded-full p-4 shadow-lg hover:bg-blue-600 transition z-[99999999999]"
          >
            ↓
          </button>
        )} */}
      </div>

      <ChatInput />
    </div>
  );
}

interface ContainerHeaderProps {
  userImage: string;
  userName: string;
}

function ContainerHeader({ userImage, userName }: ContainerHeaderProps) {
  return (
    <div className="w-full max-w-[95vw] mx-auto md:mx-0 rounded-ld bg-white dark:bg p-16 flex justify-between items-center gap-16 rounded-xl mb-24">
      <div className="flex items-center gap-8">
        <ImageCircle image={userImage} />
        <span className="text-darkBlue dark:text-white text-[14px] md:text-[18px] font-[500]">
          {userName}
        </span>
      </div>
    </div>
  );
}
